import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { LayerGroup } from "../../../../../types";

export type LayerGroupRowProps = {
  group: LayerGroup;
  isCollapsed: boolean;
  childCount: number;
  isLocked?: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onToggleCollapse: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onToggleLock?: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  children?: React.ReactNode;
};

type Props = LayerGroupRowProps;

export const LayerGroupRow: React.FC<Props> = ({
  group,
  isCollapsed,
  childCount,
  isLocked = false,
  canMoveUp,
  canMoveDown,
  onToggleCollapse,
  onRename,
  onDelete,
  onToggleLock,
  onMoveUp,
  onMoveDown,
  children,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState("");
  const [isHovered, setIsHovered] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleDoubleClick = () => {
    setEditValue(group.name);
    setIsEditing(true);
  };

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEditComplete = () => {
    if (editValue.trim() && editValue !== group.name) {
      onRename(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleEditComplete();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  return (
    <Box>
      {/* Group header row */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          px: 1,
          py: 0.5,
          cursor: "pointer",
          borderRadius: 1,
          backgroundColor: theme.palette.action.hover,
          transition: "background-color 0.15s ease",
          "&:hover": {
            backgroundColor: theme.palette.action.selected,
          },
        }}
      >
        {/* Move buttons - visible on hover, fixed width to prevent layout shift */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.15s ease",
            width: 20,
          }}
        >
          <Tooltip title="Move group up" placement="left">
            <span>
              <IconButton
                size="small"
                disabled={!canMoveUp}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp();
                }}
                sx={{ p: 0.25 }}
              >
                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move group down" placement="left">
            <span>
              <IconButton
                size="small"
                disabled={!canMoveDown}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown();
                }}
                sx={{ p: 0.25 }}
              >
                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Collapse toggle */}
        <IconButton
          size="small"
          onClick={onToggleCollapse}
          sx={{ p: 0 }}
        >
          {isCollapsed ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>

        {/* Folder icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: theme.palette.warning.main,
          }}
        >
          {isCollapsed ? (
            <FolderIcon fontSize="small" />
          ) : (
            <FolderOpenIcon fontSize="small" />
          )}
        </Box>

        {/* Group name */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <TextField
              inputRef={inputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleEditComplete}
              onKeyDown={handleKeyDown}
              size="small"
              variant="standard"
              fullWidth
              sx={{ "& input": { fontSize: "0.875rem", py: 0 } }}
            />
          ) : (
            <Tooltip title={group.name} placement="top" enterDelay={500}>
              <Typography
                variant="body2"
                noWrap
                onDoubleClick={handleDoubleClick}
                sx={{ fontWeight: 500 }}
              >
                {group.name}
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* Child count badge */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mx: 0.5 }}
        >
          ({childCount})
        </Typography>

        {/* Lock group button */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleLock?.();
          }}
          sx={{ p: 0.5 }}
        >
          {isLocked ? (
            <LockIcon fontSize="small" />
          ) : (
            <LockOpenIcon fontSize="small" />
          )}
        </IconButton>

        {/* Delete group button */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{ p: 0.5 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Children (indented) - only shown when not collapsed */}
      {!isCollapsed && (
        <Box sx={{ pl: 2 }}>
          {children}
        </Box>
      )}
    </Box>
  );
};
